import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plane, User, PawPrint, Calendar, MessageSquare, ArrowRight, ArrowLeft, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { logger } from '../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const BookTravelSafe = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    // Client Information
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    country: '',
    postalCode: '',
    
    // Pet Information
    pets: [{
      name: '',
      type: 'Dog',
      breed: '',
      age: '',
      weight: '',
      microchipNumber: '',
      healthConditions: '',
      medications: ''
    }],
    
    // Appointment Details - Availability Windows
    preferredAvailability: [],
    isFlexible: false,
    additionalNotes: ''
  });

  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handlePetChange = (index, field, value) => {
    const updatedPets = [...formData.pets];
    updatedPets[index][field] = value;
    setFormData({ ...formData, pets: updatedPets });
  };

  const handleAvailabilityToggle = (window) => {
    const currentAvailability = formData.preferredAvailability;
    if (currentAvailability.includes(window)) {
      setFormData({
        ...formData,
        preferredAvailability: currentAvailability.filter(w => w !== window)
      });
    } else {
      setFormData({
        ...formData,
        preferredAvailability: [...currentAvailability, window]
      });
    }
  };

  const availabilityWindows = [
    { value: 'monday_evening', label: 'Monday Evening (4-6 PM)' },
    { value: 'tuesday_evening', label: 'Tuesday Evening (4-6 PM)' },
    { value: 'wednesday_evening', label: 'Wednesday Evening (4-6 PM)' },
    { value: 'thursday_evening', label: 'Thursday Evening (4-6 PM)' },
    { value: 'friday_evening', label: 'Friday Evening (4-6 PM)' },
    { value: 'saturday_morning', label: 'Saturday Morning (9 AM-12 PM)' },
    { value: 'saturday_afternoon', label: 'Saturday Afternoon (12-5 PM)' },
    { value: 'weekday_mornings', label: 'Weekday Mornings (helper/nanny present)' },
    { value: 'weekday_afternoons', label: 'Weekday Afternoons (helper/nanny present)' }
  ];

  const addPet = () => {
    setFormData({
      ...formData,
      pets: [...formData.pets, {
        name: '',
        type: 'Dog',
        breed: '',
        age: '',
        weight: '',
        microchipNumber: '',
        healthConditions: '',
        medications: ''
      }]
    });
  };

  const removePet = (index) => {
    const updatedPets = formData.pets.filter((_, i) => i !== index);
    setFormData({ ...formData, pets: updatedPets });
  };

  const calculatePrice = () => {
    const numPets = formData.pets.length;
    if (numPets === 0) return 0;
    if (numPets === 1) return 169;
    if (numPets >= 5) return 685;
    
    // First pet: 169, additional pets: 129 each
    return 169 + (numPets - 1) * 129;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      // Format the message for the contact form
      const petsList = formData.pets.map((pet, i) => 
        `Pet ${i + 1}: ${pet.name} (${pet.type}, ${pet.breed}${pet.age ? `, ${pet.age}` : ''}${pet.weight ? `, ${pet.weight}` : ''})
${pet.microchipNumber ? `  Microchip: ${pet.microchipNumber}` : ''}
${pet.healthConditions ? `  Health/Behavioral: ${pet.healthConditions}` : ''}
${pet.medications ? `  Medications: ${pet.medications}` : ''}`
      ).join('\n\n');

      const availabilityText = formData.preferredAvailability
        .map(val => availabilityWindows.find(w => w.value === val)?.label)
        .filter(Boolean)
        .join(', ');

      const message = `
TravelSafe Annual Check Booking Request

Client Information:
Name: ${formData.firstName} ${formData.lastName}
Email: ${formData.email}
Phone: ${formData.phone}
Address: ${formData.address}
City: ${formData.city}
Country: ${formData.country}

Pet Information:
${petsList}

Number of Pets: ${formData.pets.length}
Estimated Price: CI$${calculatePrice()}

Availability Windows:
${availabilityText || 'Not specified'}
${formData.isFlexible ? '\n✓ Client is flexible with timing' : ''}

Additional Notes:
${formData.additionalNotes || 'None'}
      `.trim();

      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: `${formData.firstName} ${formData.lastName}`,
          email: formData.email,
          phone: formData.phone,
          serviceType: 'TravelSafe',
          pets: formData.pets,
          address: {
            street: formData.address,
            city: formData.city,
            country: formData.country,
            postalCode: formData.postalCode || ''
          },
          preferredAvailability: formData.preferredAvailability,
          isFlexible: formData.isFlexible,
          message: message,
          additionalNotes: formData.additionalNotes
        })
      });

      if (response.ok) {
        toast.success('Booking request submitted successfully! We\'ll contact you within 24 hours.');
        // Redirect to confirmation page or home
        setTimeout(() => navigate('/travelsafe?booked=true'), 2000);
      } else {
        toast.error('Failed to submit booking request. Please try again.');
      }
    } catch (error) {
      logger.error('Error submitting booking:', error);
      toast.error('An error occurred. Please try again or contact us directly.');
    } finally {
      setSubmitting(false);
    }
  };

  const nextStep = () => {
    if (currentStep === 1 && (!formData.firstName || !formData.email || !formData.address || !formData.city || !formData.country)) {
      toast.error('Please fill in all required fields');
      return;
    }
    if (currentStep === 2 && formData.pets.some(pet => !pet.name || !pet.breed)) {
      toast.error('Please enter pet name and breed for all pets');
      return;
    }
    if (currentStep === 3 && formData.preferredAvailability.length === 0) {
      toast.error('Please select at least one availability window');
      return;
    }
    setCurrentStep(currentStep + 1);
  };

  const prevStep = () => setCurrentStep(currentStep - 1);

  const steps = [
    { number: 1, title: 'Your Information', icon: User },
    { number: 2, title: 'Pet Details', icon: PawPrint },
    { number: 3, title: 'Schedule & Review', icon: Calendar }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-teal-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-teal-600 to-blue-900 text-white py-12">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-white/10 backdrop-blur-sm rounded-2xl mb-4 border border-white/20">
              <Plane className="w-8 h-8" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold mb-2">
              Schedule Your TravelSafe Check
            </h1>
            <p className="text-teal-50 text-lg">
              Get your pet travel-ready in just a few simple steps
            </p>
          </div>
        </div>
      </div>

      {/* Progress Steps */}
      <div className="bg-white border-b border-gray-200 py-6">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center justify-between">
              {steps.map((step, index) => {
                const Icon = step.icon;
                const isActive = currentStep === step.number;
                const isComplete = currentStep > step.number;
                
                return (
                  <React.Fragment key={step.number}>
                    <div className="flex flex-col items-center">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-2 transition-all ${
                        isComplete 
                          ? 'bg-green-500 text-white' 
                          : isActive 
                            ? 'bg-teal-600 text-white' 
                            : 'bg-gray-200 text-gray-400'
                      }`}>
                        {isComplete ? <Check className="w-6 h-6" /> : <Icon className="w-6 h-6" />}
                      </div>
                      <span className={`text-sm font-medium ${isActive ? 'text-teal-700' : 'text-gray-500'}`}>
                        {step.title}
                      </span>
                    </div>
                    {index < steps.length - 1 && (
                      <div className={`flex-1 h-1 mx-4 rounded ${
                        currentStep > step.number ? 'bg-green-500' : 'bg-gray-200'
                      }`} />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Form Content */}
      <div className="container mx-auto px-6 py-12">
        <form onSubmit={handleSubmit} className="max-w-4xl mx-auto">
          <div className="bg-white rounded-2xl shadow-xl p-8 mb-6">
            {/* Step 1: Client Information */}
            {currentStep === 1 && (
              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Your Information</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      First Name *
                    </label>
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      required
                      data-testid="input-first-name"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      placeholder="John"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Last Name *
                    </label>
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      required
                      data-testid="input-last-name"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      placeholder="Smith"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      data-testid="input-email"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      placeholder="john@example.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      data-testid="input-phone"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      placeholder="+1 (345) 123-4567"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Physical Address *
                    </label>
                    <input
                      type="text"
                      name="address"
                      value={formData.address}
                      onChange={handleChange}
                      required
                      data-testid="input-address"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      placeholder="123 Main Street, Apt 4B"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      City *
                    </label>
                    <input
                      type="text"
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      required
                      data-testid="input-city"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      placeholder="George Town"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Country *
                    </label>
                    <input
                      type="text"
                      name="country"
                      value={formData.country}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      placeholder="Cayman Islands"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Pet Information */}
            {currentStep === 2 && (
              <div className="space-y-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold text-gray-900">Pet Details</h2>
                  <Button
                    type="button"
                    onClick={addPet}
                    variant="outline"
                    data-testid="btn-add-pet"
                    className="flex items-center gap-2"
                  >
                    <PawPrint className="w-4 h-4" />
                    Add Another Pet
                  </Button>
                </div>

                {formData.pets.map((pet, index) => (
                  <div key={index} className="p-6 border-2 border-gray-200 rounded-xl bg-gray-50">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-lg font-semibold text-gray-900">Pet {index + 1}</h3>
                      {formData.pets.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removePet(index)}
                          className="text-red-600 hover:text-red-700 text-sm font-medium"
                        >
                          Remove
                        </button>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Pet Name *
                        </label>
                        <input
                          type="text"
                          value={pet.name}
                          onChange={(e) => handlePetChange(index, 'name', e.target.value)}
                          required
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                          placeholder="Max"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Pet Type *
                        </label>
                        <select
                          value={pet.type}
                          onChange={(e) => handlePetChange(index, 'type', e.target.value)}
                          required
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                        >
                          <option value="Dog">Dog</option>
                          <option value="Cat">Cat</option>
                          <option value="Bird">Bird</option>
                          <option value="Rabbit">Rabbit</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Breed *
                        </label>
                        <input
                          type="text"
                          value={pet.breed}
                          onChange={(e) => handlePetChange(index, 'breed', e.target.value)}
                          required
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                          placeholder="Golden Retriever"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Age (optional)
                        </label>
                        <input
                          type="text"
                          value={pet.age}
                          onChange={(e) => handlePetChange(index, 'age', e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                          placeholder="3 years"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Weight (optional)
                        </label>
                        <input
                          type="text"
                          value={pet.weight}
                          onChange={(e) => handlePetChange(index, 'weight', e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                          placeholder="25kg"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Microchip Number (if known)
                        </label>
                        <input
                          type="text"
                          value={pet.microchipNumber}
                          onChange={(e) => handlePetChange(index, 'microchipNumber', e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                          placeholder="123456789012345"
                        />
                      </div>

                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Health/Behavioral Conditions (optional)
                        </label>
                        <textarea
                          value={pet.healthConditions}
                          onChange={(e) => handlePetChange(index, 'healthConditions', e.target.value)}
                          rows={3}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                          placeholder="E.g., Anxiety around loud noises, hip dysplasia, allergies to certain foods, etc."
                        />
                      </div>

                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Current Medications (optional)
                        </label>
                        <textarea
                          value={pet.medications}
                          onChange={(e) => handlePetChange(index, 'medications', e.target.value)}
                          rows={2}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                          placeholder="E.g., Prednisone 5mg daily, Heartgard monthly, etc."
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Step 3: Schedule & Review */}
            {currentStep === 3 && (
              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Schedule & Review</h2>

                {/* Availability Windows */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900">
                    When are you available? <span className="text-red-500">*</span>
                  </h3>
                  <p className="text-sm text-gray-600">
                    Select all time windows that work for you. We'll contact you within 24 hours to confirm the best time.
                  </p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {availabilityWindows.map((window) => (
                      <label
                        key={window.value}
                        className={`flex items-center gap-3 p-4 border-2 rounded-lg cursor-pointer transition-all ${
                          formData.preferredAvailability.includes(window.value)
                            ? 'border-teal-500 bg-teal-50'
                            : 'border-gray-300 hover:border-teal-300 bg-white'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={formData.preferredAvailability.includes(window.value)}
                          onChange={() => handleAvailabilityToggle(window.value)}
                          className="w-5 h-5 text-teal-600 rounded focus:ring-teal-500"
                        />
                        <span className="text-sm font-medium text-gray-900">{window.label}</span>
                      </label>
                    ))}
                  </div>

                  {/* Flexibility Checkbox */}
                  <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.isFlexible}
                        onChange={(e) => setFormData({ ...formData, isFlexible: e.target.checked })}
                        className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500 mt-0.5"
                      />
                      <div>
                        <span className="text-sm font-semibold text-gray-900">I'm flexible with timing</span>
                        <p className="text-xs text-gray-600 mt-1">
                          Check this if you're open to alternative times and want us to suggest the best options for both of us
                        </p>
                      </div>
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Additional Notes or Questions (optional)
                  </label>
                  <textarea
                    name="additionalNotes"
                    value={formData.additionalNotes}
                    onChange={handleChange}
                    rows={4}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    placeholder="Any special requirements or concerns we should know about?"
                  />
                </div>

                {/* Summary */}
                <div className="bg-gradient-to-br from-teal-50 to-blue-50 rounded-xl p-6 border-2 border-teal-200">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">Booking Summary</h3>
                  
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-700">Client:</span>
                      <span className="font-semibold text-gray-900">{formData.firstName} {formData.lastName}</span>
                    </div>
                    
                    <div className="flex justify-between">
                      <span className="text-gray-700">Email:</span>
                      <span className="font-semibold text-gray-900">{formData.email}</span>
                    </div>

                    <div className="flex justify-between">
                      <span className="text-gray-700">Phone:</span>
                      <span className="font-semibold text-gray-900">{formData.phone}</span>
                    </div>

                    <div className="border-t border-teal-300 pt-3 mt-3">
                      <div className="flex justify-between mb-2">
                        <span className="text-gray-700">Number of Pets:</span>
                        <span className="font-semibold text-gray-900">{formData.pets.length}</span>
                      </div>
                      
                      <div className="text-sm text-gray-600 mb-3">
                        {formData.pets.map((pet, i) => (
                          <div key={i}>• {pet.name} ({pet.type})</div>
                        ))}
                      </div>
                    </div>

                    <div className="border-t border-teal-300 pt-3 mt-3">
                      <div className="flex justify-between items-center">
                        <span className="text-lg font-bold text-gray-900">Estimated Price:</span>
                        <span className="text-2xl font-bold text-teal-700" data-testid="estimated-price">CI${calculatePrice()}</span>
                      </div>
                      <p className="text-xs text-gray-600 mt-2 text-right">
                        Final price confirmed upon appointment
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Navigation Buttons */}
          <div className="flex justify-between items-center">
            <div>
              {currentStep > 1 && (
                <Button
                  type="button"
                  onClick={prevStep}
                  variant="outline"
                  className="flex items-center gap-2"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Previous
                </Button>
              )}
            </div>

            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/travelsafe')}
                data-testid="btn-cancel"
              >
                Cancel
              </Button>

              {currentStep < 3 ? (
                <Button
                  type="button"
                  onClick={nextStep}
                  data-testid="btn-next-step"
                  className="flex items-center gap-2 bg-gradient-to-r from-teal-600 to-blue-900"
                >
                  Next Step
                  <ArrowRight className="w-4 h-4" />
                </Button>
              ) : (
                <Button
                  type="submit"
                  disabled={submitting}
                  data-testid="btn-submit-booking"
                  className="flex items-center gap-2 bg-gradient-to-r from-teal-600 to-blue-900"
                >
                  {submitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Check className="w-4 h-4" />
                      Submit Booking Request
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};
